import React, { useEffect } from 'react';
import { Box, Divider, IconButton, Tooltip } from '@mui/material';
import FormatBoldIcon from '@mui/icons-material/FormatBold';
import FormatItalicIcon from '@mui/icons-material/FormatItalic';
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted';
import FormatListNumberedIcon from '@mui/icons-material/FormatListNumbered';
import FormatQuoteIcon from '@mui/icons-material/FormatQuote';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import UndoIcon from '@mui/icons-material/Undo';
import RedoIcon from '@mui/icons-material/Redo';
import TitleIcon from '@mui/icons-material/Title';
import { EditorContent, useEditor, Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { normalizeRichText } from './rich-text-utils';

interface RichTextEditorProps {
    value?: string;
    onChange: (html: string) => void;
    error?: boolean;
    minHeight?: number;
}

function ToolbarButton({ title, active, disabled, onClick, children }: {
    title: string; active?: boolean; disabled?: boolean; onClick: () => void; children: React.ReactNode;
}) {
    return (
        <Tooltip title={title}>
            <span>
                <IconButton
                    size="small"
                    onClick={onClick}
                    disabled={disabled}
                    color={active ? 'primary' : 'default'}
                    sx={{ borderRadius: 1, bgcolor: active ? 'action.selected' : 'transparent' }}
                >
                    {children}
                </IconButton>
            </span>
        </Tooltip>
    );
}

function Toolbar({ editor }: { editor: Editor }) {
    function handleLink() {
        const previous = editor.getAttributes('link').href as string | undefined;
        const url = window.prompt('Endereço do link (URL)', previous || 'https://');
        if (url === null) return;
        if (url === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            return;
        }
        editor.chain().focus().extendMarkRange('link').setLink({ href: url }).run();
    }

    return (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 0.25, p: 0.5, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'background.default' }}>
            <ToolbarButton title="Negrito" active={editor.isActive('bold')} onClick={() => editor.chain().focus().toggleBold().run()}>
                <FormatBoldIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton title="Itálico" active={editor.isActive('italic')} onClick={() => editor.chain().focus().toggleItalic().run()}>
                <FormatItalicIcon fontSize="small" />
            </ToolbarButton>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <ToolbarButton title="Título" active={editor.isActive('heading', { level: 2 })} onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}>
                <TitleIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton title="Subtítulo" active={editor.isActive('heading', { level: 3 })} onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}>
                <TitleIcon sx={{ fontSize: 16 }} />
            </ToolbarButton>
            <ToolbarButton title="Citação" active={editor.isActive('blockquote')} onClick={() => editor.chain().focus().toggleBlockquote().run()}>
                <FormatQuoteIcon fontSize="small" />
            </ToolbarButton>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <ToolbarButton title="Lista" active={editor.isActive('bulletList')} onClick={() => editor.chain().focus().toggleBulletList().run()}>
                <FormatListBulletedIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton title="Lista numerada" active={editor.isActive('orderedList')} onClick={() => editor.chain().focus().toggleOrderedList().run()}>
                <FormatListNumberedIcon fontSize="small" />
            </ToolbarButton>
            <Divider orientation="vertical" flexItem sx={{ mx: 0.5 }} />
            <ToolbarButton title="Inserir link" active={editor.isActive('link')} onClick={handleLink}>
                <LinkIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton title="Remover link" disabled={!editor.isActive('link')} onClick={() => editor.chain().focus().unsetLink().run()}>
                <LinkOffIcon fontSize="small" />
            </ToolbarButton>
            <Box sx={{ flex: 1 }} />
            <ToolbarButton title="Desfazer" disabled={!editor.can().undo()} onClick={() => editor.chain().focus().undo().run()}>
                <UndoIcon fontSize="small" />
            </ToolbarButton>
            <ToolbarButton title="Refazer" disabled={!editor.can().redo()} onClick={() => editor.chain().focus().redo().run()}>
                <RedoIcon fontSize="small" />
            </ToolbarButton>
        </Box>
    );
}

export function RichTextEditor({ value, onChange, error, minHeight = 180 }: RichTextEditorProps) {
    const editor = useEditor({
        extensions: [
            StarterKit.configure({ heading: { levels: [2, 3] } }),
            Link.configure({ openOnClick: false, autolink: true }),
        ],
        content: value || '',
        onUpdate: ({ editor }) => onChange(normalizeRichText(editor.getHTML())),
    });

    // Sincroniza quando o valor muda por fora (ex.: abrir o diálogo com outro registro)
    useEffect(() => {
        if (!editor) return;
        const current = normalizeRichText(editor.getHTML());
        if ((value || '') !== current) {
            editor.commands.setContent(value || '', false);
        }
    }, [value, editor]);

    return (
        <Box
            sx={{
                border: '1px solid',
                borderColor: error ? 'error.main' : 'rgba(0, 0, 0, 0.23)',
                borderRadius: 1,
                overflow: 'hidden',
                '&:hover': { borderColor: error ? 'error.main' : 'text.primary' },
                '&:focus-within': { borderColor: error ? 'error.main' : 'primary.main', boxShadow: (theme) => `0 0 0 1px ${error ? theme.palette.error.main : theme.palette.primary.main}` },
            }}
        >
            {editor && <Toolbar editor={editor} />}
            <Box
                sx={{
                    px: 1.75,
                    '& .ProseMirror': { minHeight, outline: 'none', py: 1.5, fontSize: '0.95rem' },
                    '& .ProseMirror p': { my: 0.75 },
                    '& .ProseMirror h2': { fontSize: '1.35rem', my: 1 },
                    '& .ProseMirror h3': { fontSize: '1.1rem', my: 1 },
                    '& .ProseMirror blockquote': { borderLeft: '3px solid', borderColor: 'divider', pl: 1.5, ml: 0, color: 'text.secondary' },
                    '& .ProseMirror a': { color: 'primary.main' },
                }}
            >
                <EditorContent editor={editor} />
            </Box>
        </Box>
    );
}
